import { useEffect, useCallback, useState } from "react";
import type { CardItem } from "../../interfaces/componentInterfaces/cards";
import Cards from "../cards/Cards";
import PageHeader from "../pageHeader/PageHeader";
import Table from "../table/Table";
import { useFiltersStore } from "../../stores/filters.store";
import { useStatisticsStore } from "../../stores/statistics.store";
import { useJobsStore } from "../../stores/jobs.store";
import { useResources } from "../../hooks/useResources";
import { convertToCSV, downloadCSV } from "../../helpers/formatters";
import { globalConfig } from "../../config/globalConfig";
import "./LandingPage.scss";

const LandingPage = () => {
  const { getResourceValue } = useResources();
  const [isCSVQueued, setIsCSVQueued] = useState(false);

  // Use separate stores
  const {
    selectedCountryIsoCode,
    selectedGradeId,
    selectedLanguageIsoCode,
    selectedSubjectId,
    selectedPendingGraphics,
    searchText,
    searchType,
    currentPage,
    pageSize,
    resetFilters,
  } = useFiltersStore();

  const {
    statistics,
    isLoading: isLoadingStatistics,
    load: loadStatistics,
  } = useStatisticsStore();

  const {
    jobs,
    pagination,
    isLoading: isLoadingJobs,
    load: loadJobs,
  } = useJobsStore();

  // Load statistics when filters change
  useEffect(() => {
    loadStatistics(selectedGradeId, selectedSubjectId, selectedCountryIsoCode, selectedLanguageIsoCode, "create");
  }, [selectedGradeId, selectedSubjectId, selectedCountryIsoCode, selectedLanguageIsoCode, loadStatistics]);

  // Load jobs when filters or pagination change
  useEffect(() => {
    loadJobs({
      page_number: currentPage,
      page_size: pageSize,
      grade_id: selectedGradeId,
      subject_id: selectedSubjectId,
      country_iso_code: selectedCountryIsoCode,
      language_iso_code: selectedLanguageIsoCode,
      pending_graphics: selectedPendingGraphics,
      search_text: searchText,
      search_type: searchType,
      job_type: "create",
    });
    // Re-enable CSV button when filters change
    setIsCSVQueued(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    selectedGradeId,
    selectedSubjectId,
    selectedCountryIsoCode,
    selectedLanguageIsoCode,
    selectedPendingGraphics,
    searchText,
    loadJobs,
  ]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    // Reset all filters to initial state
    resetFilters();
    
    // Load statistics and jobs with default filters (null values)
    loadStatistics(null, null, null, null, "create");
    loadJobs({
      page_number: globalConfig.defaultPageNumber,
      page_size: globalConfig.defaultPageSize,
      grade_id: null,
      subject_id: null,
      country_iso_code: null,
      language_iso_code: null,
      pending_graphics: null,
      search_text: null,
      search_type: null,
      job_type: "create",
    });
  }, [resetFilters, loadStatistics, loadJobs]);

    // CSV Download handler
  const handleCSVDownload = useCallback(() => {
    // Format date to readable format
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    // Transform jobs data to match table columns
    const csvData = jobs.map(job => ({
      'Job ID': job.job_id,
      'User Email': job.user_email,
      'File Name': job.file_name,
      'Subject': job.subject_title || '',
      'Upload Date and Time': formatDate(job.job_created_at_utc),
      'Update Date and Time': formatDate(job.job_updated_at_utc),
      'Uploading Status - Total': job.job_status_count?.total_count ?? 0,
      'Uploading Status - Success': job.job_status_count?.success_count ?? 0,
      'Uploading Status - Failed': job.job_status_count?.failed_count ?? 0,
      'Graphics Status - Total': job.job_graphics_status_count?.total_count ?? 0,
      'Graphics Status - Success': job.job_graphics_status_count?.success_count ?? 0,
      'Graphics Status - Failed': job.job_graphics_status_count?.failed_count ?? 0,
      'Published Questions': job.job_status_count?.published_count ?? 0,
    }));

    // Convert to CSV (will include headers even if data is empty)
    const csvContent = convertToCSV(csvData);
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `jobs-${timestamp}.csv`;
    
    // Trigger download
    downloadCSV(csvContent, filename);
  }, [jobs]);
  // Convert statistics to cards
  const dashboardCards: CardItem[] = statistics
    ? [
        {
          id: "1",
          title: getResourceValue("total_questions_upload", "Total Questions Upload"),
          value: statistics.total_questions,
        },
        {
          id: "2",
          title: getResourceValue("questions_succeed", "Questions Succeed"),
          value: statistics.succeeded_questions,
        },
        {
          id: "3",
          title: getResourceValue("questions_failed", "Questions Failed"),
          value: statistics.failed_questions,
        },
        {
          id: "4",
          title: getResourceValue("questions_pending_graphics", "Questions Pending Graphics"),
          value: statistics.pending_graphics_questions,
        },
        {
          id: "5",
          title: getResourceValue("questions_published", "Questions Published"),
          value: statistics.published_questions,
        },
      ]
    : [];

  return (
    <div className="page">
      <PageHeader onRefresh={handleRefresh} />
      
      {isLoadingStatistics ? (
        <div className="loading-cards">{getResourceValue("loading_statistics", "Loading statistics...")}</div>
      ) : (
        <Cards cards={dashboardCards} />
      )}
      
      <Table 
        jobs={jobs}
        pagination={pagination}
        isLoading={isLoadingJobs}
        onCSVDownload={handleCSVDownload}
        isCSVDisabled={isCSVQueued}
        jobType="create"
      />
    </div>
  );
};

export default LandingPage;
